/**
 * NIGHTBOUND - choose your starter.
 *
 * Four creatures, one choice, no take-backs. The list and the starting items
 * come from the server catalogue; the name is the only thing the player types.
 */

import { useEffect, useMemo, useState } from 'react'
import { getItem } from '../../shared/items'
import type { SpeciesId } from '../../shared/species'
import { useGame } from '../state/GameProvider'
import { SpeciesCard } from '../components/SpeciesCard'
import { Logo } from '../components/Logo'
import { Button, Chip, Panel, PanelHeading } from '../components/ui'

const NAME_MAX = 24

export function StarterScreen() {
  const { catalog, createCreature, error, dismissError } = useGame()
  const [selected, setSelected] = useState<SpeciesId | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const speciesList = catalog?.species ?? []
  const startingItems = useMemo(() => catalog?.startingInventory ?? [], [catalog])

  useEffect(() => {
    if (!selected && speciesList[0]) setSelected(speciesList[0].id)
  }, [selected, speciesList])

  const selectedSpecies = speciesList.find((s) => s.id === selected) ?? null
  const nameValid = name.trim().length >= 1 && name.trim().length <= NAME_MAX

  const submit = async () => {
    if (!selected || !nameValid || busy) return
    dismissError()
    setBusy(true)
    try {
      await createCreature({ species: selected, name: name.trim() })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative min-h-[100svh]">
      <div aria-hidden className="nb-room absolute inset-0 -z-10" />
      <div aria-hidden className="nb-vignette absolute inset-0 -z-10" />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-6 space-y-3">
          <Logo size="sm" tagline="The shelter is empty" />
          <h1 className="font-display text-2xl text-bone-100 sm:text-3xl">
            Something has been waiting for you.
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-ash-400">
            Choose the creature you will take down into the dark. They differ more than they look:
            how fast they tire, how much they trust, and when they need to sleep. You cannot change
            your mind later.
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
          <div className="grid gap-4 sm:grid-cols-2">
            {speciesList.map((species) => (
              <SpeciesCard
                key={species.id}
                species={species}
                selected={selected === species.id}
                onSelect={() => setSelected(species.id)}
              />
            ))}
          </div>

          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            <Panel className="overflow-hidden">
              <PanelHeading>Name it</PanelHeading>
              <div className="space-y-3 p-4">
                <label htmlFor="creature-name" className="block space-y-1.5">
                  <span className="nb-label text-[9px] text-ash-300">
                    {selectedSpecies ? `${selectedSpecies.name} name` : 'Name'}
                  </span>
                  <input
                    id="creature-name"
                    type="text"
                    maxLength={NAME_MAX}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Something it will answer to"
                    className="w-full rounded-[3px] border border-ash-600/60 bg-night-950/70 px-3 py-2.5 text-sm text-bone-200 placeholder:text-ash-500 focus:border-ember-600/70 focus:outline-none"
                  />
                </label>
                <p className="nb-numeric text-right text-[10px] text-ash-500">
                  {name.trim().length}/{NAME_MAX}
                </p>

                {selectedSpecies ? (
                  <div className="rounded-[3px] border border-ash-700/40 bg-night-950/50 p-3">
                    <p className="font-display text-[11px] italic leading-relaxed text-ash-300">
                      {selectedSpecies.description}
                    </p>
                  </div>
                ) : null}

                {error ? (
                  <p role="alert" className="rounded-[3px] border border-blood-500/50 bg-blood-950/60 px-3 py-2 text-xs text-blood-400">
                    {error}
                  </p>
                ) : null}

                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={!selected || !nameValid || busy}
                  onClick={() => void submit()}
                >
                  {busy ? 'Bringing it home…' : 'Take it home'}
                </Button>
              </div>
            </Panel>

            <Panel className="overflow-hidden">
              <PanelHeading right={<Chip tone="neutral">starter kit</Chip>}>You begin with</PanelHeading>
              <ul className="divide-y divide-ash-700/30">
                {startingItems.map((entry) => {
                  const item = getItem(entry.itemKey)
                  if (!item) return null
                  return (
                    <li key={entry.itemKey} className="flex items-center justify-between gap-3 px-4 py-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span aria-hidden className="text-base">
                          {item.glyph}
                        </span>
                        <span className="truncate text-xs text-bone-300">{item.name}</span>
                      </span>
                      <span className="nb-numeric shrink-0 text-xs text-ash-400">×{entry.quantity}</span>
                    </li>
                  )
                })}
              </ul>
            </Panel>
          </aside>
        </div>
      </div>
    </div>
  )
}
